import React, { forwardRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { Theme } from '@constants';
import { useContextReference, useIndexedStateReference, useNodeHandle, useNodeHandleFromIndex } from '@hooks';

import { TWCButton } from '../../focusable/TWCButton/TWCButton';

export const DebugPickerValues = {
  decimal: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
  month: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
  days: Array.from({ length: 31 }, (_value, index) => `${index + 1}`),
};

/**
 * Component to allow selecting a value given a range of values.
 * Values is an array of arrays, where each array contains picker values. For example, to allow
 * a two digit decimal picker, pass in:
 * values = [DebugPickerValues.decimal, DebugPickerValues.decimal]
 * The different picker values are combined into one value and each time it updates the onValueChanged
 * callback is called with the new value, combining all picker values.
 */
export const DebugPicker = forwardRef(
  (
    {
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      initialValue,
      testID,
      values = [],
      onValueChanged = () => {},
      shouldUseContextReference = true,
      width = 50,
    },
    reference,
  ) => {
    const getInitialIndicies = () =>
      initialValue
        ? values.map((value, index) => {
            const thisIndex = values.length > 1 ? value.indexOf(initialValue[index]) : value.indexOf(initialValue);

            return thisIndex >= 0 ? thisIndex : 0;
          })
        : values.map(() => 0);

    const [indicies, setIndicies] = useState(getInitialIndicies());
    const [stateReferences, , setStateReferences] = useIndexedStateReference({});
    const contextReference = useContextReference(testID);
    const firstReference = shouldUseContextReference ? contextReference : reference;

    const getValue = currentIndicies => {
      if (values.length === 0) {
        return 'No values';
      }
      return currentIndicies.map((valueIndex, thisIndex) => values[thisIndex][valueIndex]).join('');
    };

    return (
      <View style={styles.wrapper}>
        {values.map((value, index) => (
          <PickerItem
            key={index}
            count={values.length}
            firstReference={firstReference}
            index={index}
            nextFocusDown={nextFocusDown}
            nextFocusLeft={nextFocusLeft}
            nextFocusRight={nextFocusRight}
            nextFocusUp={nextFocusUp}
            ref={thisReference => {
              if (index !== 0) {
                setStateReferences(thisReference, index);
              }
            }}
            references={stateReferences}
            shouldUseContextReference={shouldUseContextReference}
            testID={testID}
            title={value[indicies[index]]}
            value={value}
            width={width}
            onPress={() => {
              if (value && value.length > 0) {
                const newIndicies = [...indicies];
                const newIndex = (indicies[index] + 1) % value.length;

                newIndicies[index] = newIndex;
                onValueChanged(getValue(newIndicies));
                setIndicies(newIndicies);
              }
            }}
          />
        ))}
      </View>
    );
  },
);

const PickerItem = forwardRef(
  (
    {
      value,
      testID,
      title,
      width,
      count,
      index,
      references,
      firstReference,
      nextFocusDown,
      nextFocusUp,
      nextFocusLeft,
      nextFocusRight,
      onPress,
      shouldUseContextReference,
    },
    reference,
  ) => {
    const isLast = index === count - 1;
    const isFirst = index === 0;

    const itemNextHandle = useNodeHandleFromIndex(references, isLast ? null : index + 1);

    const itemPreviousHandle = useNodeHandleFromIndex(references, index > 1 ? index - 1 : null);

    const firstHandle = useNodeHandle(firstReference);

    return (
      <TWCButton
        key={`${index}`}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={isFirst ? nextFocusLeft : index === 1 ? firstHandle : itemPreviousHandle}
        nextFocusRight={isLast ? nextFocusRight : itemNextHandle}
        nextFocusUp={nextFocusUp}
        ref={isFirst ? firstReference : reference}
        shouldUseContextReference={isFirst ? shouldUseContextReference : false}
        style={styles.button}
        testID={testID}
        title={title}
        width={width}
        onPress={onPress}
      />
    );
  },
);

const styles = StyleSheet.create({
  button: {
    marginLeft: Theme.px(10),
  },
  wrapper: {
    alignItems: 'center',
    flexDirection: 'row',
  },
});
